<template>
  <v-card-text class="pdp-tabs-wrapper">
    <v-data-table
      :headers="meetingHeaders"
      :items="meetingRows"
      :loading="loading"
      loading-text="Loading performance meetings..."
      :items-per-page="-1"
      hide-default-footer
      dense
      class="meeting-table"
      item-key="Meeting_ID"
    >
      <template v-slot:body="{ items }">
        <tbody v-for="item in items" :key="`meeting-group-${item.Meeting_ID}`">
            <tr
              class="meeting-row"
              :class="{ 'meeting-row--expanded': isMeetingExpanded(item.Meeting_ID) }"
              @click="toggleMeeting(item)"
            >
              <td class="meeting-date-cell">
                <v-icon small class="mr-2 meeting-expand-icon">
                  {{ isMeetingExpanded(item.Meeting_ID) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                </v-icon>
                <span>{{ formatDateTime(item.Start_Time) }}</span>
                <v-spacer></v-spacer>
                <v-btn
                  v-if="canManageMeetings"
                  icon
                  x-small
                  class="delete-icon-btn ml-2"
                  @click.stop="triggerRemoveMeeting(item)"
                >
                  <v-icon class="action-delete-icon">mdi-delete-outline</v-icon>
                </v-btn>
              </td>
              <td>
                <div class="period-cell">
                  <span>{{ item.periodLabel }}</span>
                  <v-btn
                    v-if="canManageMeetings"
                    icon
                    small
                    class="period-edit-btn ml-1"
                    @click.stop="openPeriodDialog(item)"
                  >
                    <v-icon small class="action-pencil-icon">mdi-pencil</v-icon>
                  </v-btn>
                </div>
              </td>
              <td class="text-center font-weight-bold">{{ item.progress }}%</td>
              <td class="text-center">{{ item.lastUpdated }}</td>
            </tr>
            <tr
              v-if="isMeetingExpanded(item.Meeting_ID)"
              class="expanded-row"
            >
              <td :colspan="meetingHeaders.length" class="expanded-cell pa-0">
                <v-tabs
                  :value="getMeetingTab(item.Meeting_ID)"
                  show-arrows
                  class="expanded-tabs"
                  @change="setMeetingTab(item.Meeting_ID, $event)"
                >
                  <v-tab>Feedback</v-tab>
                  <v-tab>Objectives</v-tab>
                  <v-tab v-if="canViewSummary">Summary</v-tab>
                </v-tabs>
                <v-tabs-items
                  :value="getMeetingTab(item.Meeting_ID)"
                  class="expanded-tab-content"
                  @change="setMeetingTab(item.Meeting_ID, $event)"
                >
                  <v-tab-item eager>
                    <PdpFeedbackTab
                      :key="`feedback-${item.Meeting_ID}`"
                      :employee="employee"
                      :rolesOfUser="rolesOfUser"
                      :selectedMeeting="item"
                      @pdp-period-metadata-changed="refreshPeriodsMetadata"
                    />
                  </v-tab-item>
                  <v-tab-item eager>
                    <PdpTab
                      :key="`objectives-${item.Meeting_ID}`"
                      :employee="employee"
                      :rolesOfUser="rolesOfUser"
                      :selectedMeeting="item"
                      @objective-updated="handleObjectiveUpdated"
                      @pdp-period-metadata-changed="refreshPeriodsMetadata"
                    />
                  </v-tab-item>
                  <v-tab-item v-if="canViewSummary" eager>
                    <PdpSummaryTab
                      :key="`summary-${item.Meeting_ID}`"
                      :employee="employee"
                      :rolesOfUser="rolesOfUser"
                      :selectedMeeting="item"
                      @pdp-period-metadata-changed="refreshPeriodsMetadata"
                    />
                  </v-tab-item>
                </v-tabs-items>
              </td>
            </tr>
        </tbody>
      </template>
    </v-data-table>

    <v-dialog v-model="showPeriodDialog" max-width="600">
      <v-card>
        <v-card-title>{{ editingPeriod && editingPeriod.Period_ID ? 'Edit Period' : 'Add Period' }}</v-card-title>
        <v-card-text>
          <v-select
            v-model="periodForm.cycleMonths"
            :items="cycleOptions"
            item-text="text"
            item-value="value"
            label="Cycle"
            dense
            solo
            hide-details
            class="mb-4"
            @change="applyCycleEndDate"
          ></v-select>
          <v-menu
            v-model="startDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="periodForm.startDate"
                label="Start Date"
                prepend-icon="mdi-calendar"
                readonly
                dense
                solo
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="periodForm.startDate"
              @input="onStartDateInput"
            ></v-date-picker>
          </v-menu>
          <v-menu
            v-model="endDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="periodForm.endDate"
                label="End Date"
                prepend-icon="mdi-calendar"
                readonly
                dense
                solo
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="periodForm.endDate"
              @input="endDateMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closePeriodDialog">Cancel</v-btn>
          <v-btn color="primary" @click="savePeriod">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmOrDeniedDialog
      v-if="showDeleteMeetingDialog"
      v-model="showDeleteMeetingDialog"
      :mode="confirmOrDeniedDialogModes.delete"
      :message="deleteMeetingMessage"
      @onDeleteClick="removeMeeting"
      @onCancelClick="cancelRemoveMeeting"
      @onCloseClick="cancelRemoveMeeting"
    />

  </v-card-text>
</template>

<script>
import { getApiErrorMessage } from '@/utils/apiError'
export default {
  name: 'PdpTabs',
  components: {
    PdpFeedbackTab: () => import('@/components/employee/employee_details_tabs/PdpFeedbackTab.vue'),
    PdpTab: () => import('@/components/employee/employee_details_tabs/PdpTab.vue'),
    PdpSummaryTab: () => import('@/components/employee/employee_details_tabs/PdpSummaryTab.vue'),
    ConfirmOrDeniedDialog: () => import('@/components/global/ConfirmOrDeniedDialog.vue')
  },
  props: {
    employee: {
      required: true,
      type: Object
    },
    rolesOfUser: {
      required: false,
      type: Array,
      default: () => []
    },
    canViewSummary: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      loading: false,
      meetings: [],
      periods: [],
      objectivesByPeriod: {},
      expandedMeetingIds: [],
      activeTabs: {},
      showPeriodDialog: false,
      startDateMenu: false,
      endDateMenu: false,
      editingPeriod: null,
      periodForm: {
        startDate: null,
        endDate: null,
        meetingId: null,
        cycleMonths: 6
      },
      cycleOptions: [
        { text: '6 months', value: 6 },
        { text: '12 months', value: 12 }
      ],
      meetingHeaders: [
        { text: 'Meeting Date', value: 'meetingDate', sortable: false, class: 'text-left' },
        { text: 'Period', value: 'period', sortable: false, class: 'text-left' },
        { text: 'Progress', value: 'progress', sortable: false, align: 'center', class: 'text-center' },
        { text: 'Last Updated', value: 'lastUpdated', sortable: false, align: 'center', class: 'text-center' }
      ],
      showDeleteMeetingDialog: false,
      meetingToDelete: null,
      deleteMeetingMessage: ''
    }
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    confirmOrDeniedDialogModes () {
      return this.$store.getters.getConfirmOrDeniedDialogModes
    },
    canManageMeetings () {
      return this.rolesOfUser.some(role => ['HR', 'financier', 'manager'].includes(role.RoleName))
    },
    periodByMeetingId () {
      const map = {}
      for (const period of this.periods) {
        if (!period) continue
        const meetingId = period.Meeting_ID || period.Meeting_ID_From_Meeting
        if (!meetingId) continue
        map[meetingId] = period
      }
      return map
    },
    meetingRows () {
      return this.meetings.map(meeting => {
        const period = this.periodByMeetingId[meeting.Meeting_ID]
        return {
          ...meeting,
          periodRaw: period || null,
          periodLabel: this.resolvePeriodLabel(period, meeting.Start_Time),
          progress: period ? this.calculateProgress(period.Period_ID) : 0,
          lastUpdated: period
            ? this.formatDateTime(period.Last_Activity_At || period.Updated_At || period.Created_At)
            : '-'
        }
      })
    }
  },
  watch: {
    'employee.Employee_ID': {
      immediate: true,
      handler (employeeId) {
        if (employeeId) {
          this.loadData()
        } else {
          this.meetings = []
          this.periods = []
          this.objectivesByPeriod = {}
          this.expandedMeetingIds = []
          this.activeTabs = {}
        }
      }
    }
  },
  methods: {
    async loadData () {
      this.loading = true
      this.expandedMeetingIds = []
      this.activeTabs = {}
      try {
        const [meetingsResponse, periodsResponse] = await Promise.all([
          this.axios.get(`/api/pdp/meetings/${this.employee.Employee_ID}`, {
            headers: { Authorization: 'Bearer ' + this.user.token }
          }),
          this.axios.get(`/api/pdp/periods/${this.employee.Employee_ID}`, {
            headers: { Authorization: 'Bearer ' + this.user.token }
          })
        ])

        this.meetings = Array.isArray(meetingsResponse.data) ? meetingsResponse.data : []
        this.periods = Array.isArray(periodsResponse.data) ? periodsResponse.data : []

        const periodIds = this.periods
          .filter(period => period && period.Period_ID)
          .map(period => period.Period_ID)

        await Promise.all(periodIds.map(periodId => this.loadObjectives(periodId)))
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      } finally {
        this.loading = false
      }
    },
    async loadObjectives (periodId) {
      try {
        const response = await this.axios.get(`/api/pdp/periods/${periodId}/objectives`, {
          headers: { Authorization: 'Bearer ' + this.user.token }
        })
        this.$set(this.objectivesByPeriod, periodId, Array.isArray(response.data) ? response.data : [])
      } catch (_) {
        this.$set(this.objectivesByPeriod, periodId, [])
      }
    },
    async refreshPeriodsMetadata () {
      if (!this.employee || !this.employee.Employee_ID) return
      try {
        const response = await this.axios.get(`/api/pdp/periods/${this.employee.Employee_ID}`, {
          headers: { Authorization: 'Bearer ' + this.user.token }
        })
        this.periods = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    isMeetingExpanded (meetingId) {
      return this.expandedMeetingIds.includes(meetingId)
    },
    toggleMeeting (meeting) {
      if (this.isMeetingExpanded(meeting.Meeting_ID)) {
        this.expandedMeetingIds = this.expandedMeetingIds.filter(id => id !== meeting.Meeting_ID)
        return
      }
      this.expandedMeetingIds = [...this.expandedMeetingIds, meeting.Meeting_ID]
      if (typeof this.activeTabs[meeting.Meeting_ID] !== 'number') {
        this.$set(this.activeTabs, meeting.Meeting_ID, 0)
      }
    },
    getMeetingTab (meetingId) {
      return typeof this.activeTabs[meetingId] === 'number' ? this.activeTabs[meetingId] : 0
    },
    setMeetingTab (meetingId, tabIndex) {
      this.$set(this.activeTabs, meetingId, tabIndex)
    },
    calculateProgress (periodId) {
      if (!periodId) return 0
      const objectives = this.objectivesByPeriod[periodId] || []
      const mainObjectives = objectives.filter(objective => objective.Type === 'Main')
      if (!mainObjectives.length) return 0
      const doneObjectives = mainObjectives.filter(objective => objective.Status === 'Done')
      return Math.round((doneObjectives.length / mainObjectives.length) * 100)
    },
    handleObjectiveUpdated (payload) {
      if (!payload || !payload.periodId || !payload.objectiveId) return
      const periodId = payload.periodId
      const list = this.objectivesByPeriod[periodId] || []
      const idx = list.findIndex(obj => String(obj.Objective_ID) === String(payload.objectiveId))
      if (idx === -1) return
      const updatedList = [...list]
      updatedList[idx] = {
        ...updatedList[idx],
        ...payload.objective
      }
      this.$set(this.objectivesByPeriod, periodId, updatedList)
    },
    formatPeriod (startDate, endDate) {
      if (!startDate) return '-'
      const start = new Date(startDate)
      const end = endDate ? new Date(endDate) : null
      const startText = `${String(start.getDate()).padStart(2, '0')}/${String(start.getMonth() + 1).padStart(2, '0')}/${start.getFullYear()}`
      if (!end) return startText
      const endText = `${String(end.getDate()).padStart(2, '0')}/${String(end.getMonth() + 1).padStart(2, '0')}/${end.getFullYear()}`
      return `${startText} - ${endText}`
    },
    resolvePeriodLabel (period, meetingStartTime) {
      if (period && period.Start_Date) {
        return this.formatPeriod(period.Start_Date, period.End_Date)
      }
      if (!meetingStartTime) return '-'
      const meetingDate = new Date(meetingStartTime)
      const start = new Date(meetingDate.getFullYear(), meetingDate.getMonth() + 1, 1)
      const end = new Date(start.getFullYear(), start.getMonth() + 6, 0)
      return this.formatPeriod(start, end)
    },
    formatDateTime (dateString) {
      if (!dateString) return '-'
      let date
      if (typeof dateString === 'string') {
        const s = dateString.trim()
        if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?(\.\d+)?$/.test(s)) {
          date = new Date(`${s.replace(' ', 'T')}Z`)
        } else {
          date = new Date(s)
        }
      } else {
        date = new Date(dateString)
      }
      if (Number.isNaN(date.getTime())) return '-'
      return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    },
    triggerRemoveMeeting (meeting) {
      if (!meeting || !meeting.Meeting_ID) return
      this.meetingToDelete = meeting
      this.deleteMeetingMessage = `Delete meeting on ${this.formatDateTime(meeting.Start_Time)}?`
      this.showDeleteMeetingDialog = true
    },
    cancelRemoveMeeting () {
      this.showDeleteMeetingDialog = false
      this.meetingToDelete = null
      this.deleteMeetingMessage = ''
    },
    async removeMeeting () {
      const meeting = this.meetingToDelete
      this.showDeleteMeetingDialog = false
      this.meetingToDelete = null
      this.deleteMeetingMessage = ''

      if (!meeting || !meeting.Meeting_ID) return
      try {
        await this.axios.delete(`/api/pdp/meetings/${meeting.Meeting_ID}`, {
          headers: { Authorization: 'Bearer ' + this.user.token }
        })
        this.expandedMeetingIds = this.expandedMeetingIds.filter(id => id !== meeting.Meeting_ID)
        this.$delete(this.activeTabs, meeting.Meeting_ID)
        await this.loadData()
        this.$store.dispatch('setSuccess', { successMsg: 'Meeting deleted successfully' })
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    },
    openPeriodDialog (meetingRow) {
      this.editingPeriod = meetingRow.periodRaw
      const period = meetingRow.periodRaw
      const startDate = period && period.Start_Date ? this.toDateInputValue(period.Start_Date) : null
      const endDate = period && period.End_Date ? this.toDateInputValue(period.End_Date) : null
      this.periodForm = {
        startDate,
        endDate,
        meetingId: (period && (period.Meeting_ID || period.Meeting_ID_From_Meeting)) || meetingRow.Meeting_ID,
        cycleMonths: this.inferCycleMonths(startDate, endDate)
      }
      this.showPeriodDialog = true
    },
    closePeriodDialog () {
      this.showPeriodDialog = false
      this.editingPeriod = null
      this.periodForm = {
        startDate: null,
        endDate: null,
        meetingId: null,
        cycleMonths: 6
      }
    },
    toDateInputValue (value) {
      if (!value) return null
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        return value.slice(0, 10)
      }
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return null
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    computePeriodEndDate (startDate, cycleMonths) {
      if (!startDate) return null
      const parts = String(startDate).split('-').map(Number)
      if (parts.length < 3 || !parts[0] || !parts[1]) return null
      const [year, month] = parts
      const months = Number(cycleMonths) === 12 ? 12 : 6
      const end = new Date(year, month - 1 + months, 0)
      const endYear = end.getFullYear()
      const endMonth = String(end.getMonth() + 1).padStart(2, '0')
      const endDay = String(end.getDate()).padStart(2, '0')
      return `${endYear}-${endMonth}-${endDay}`
    },
    inferCycleMonths (startDate, endDate) {
      if (!startDate || !endDate) return 6
      const start = new Date(`${startDate}T00:00:00`)
      const end = new Date(`${endDate}T00:00:00`)
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 6
      const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1
      return months >= 11 ? 12 : 6
    },
    applyCycleEndDate () {
      if (!this.periodForm.startDate) return
      this.periodForm.endDate = this.computePeriodEndDate(this.periodForm.startDate, this.periodForm.cycleMonths)
    },
    onStartDateInput () {
      this.startDateMenu = false
      this.applyCycleEndDate()
    },
    async savePeriod () {
      if (!this.periodForm.startDate) {
        this.$store.dispatch('setError', { errorMsg: 'Start date is required' })
        return
      }
      try {
        if (this.editingPeriod && this.editingPeriod.Period_ID) {
          await this.axios.put(`/api/pdp/periods/${this.editingPeriod.Period_ID}`, {
            startDate: this.periodForm.startDate,
            endDate: this.periodForm.endDate
          }, {
            headers: { Authorization: 'Bearer ' + this.user.token }
          })
        } else {
          await this.axios.post('/api/pdp/periods', {
            meetingId: this.periodForm.meetingId,
            employeeId: this.employee.Employee_ID,
            startDate: this.periodForm.startDate,
            endDate: this.periodForm.endDate
          }, {
            headers: { Authorization: 'Bearer ' + this.user.token }
          })
        }
        const currentExpandedMeetingIds = [...this.expandedMeetingIds]
        await this.loadData()
        this.expandedMeetingIds = currentExpandedMeetingIds
        this.closePeriodDialog()
        this.$store.dispatch('setSuccess', { successMsg: 'Period saved successfully' })
      } catch (error) {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      }
    }
  }
}
</script>

<style scoped>
.pdp-tabs-wrapper {
  padding: 0;
}

.meeting-table >>> .v-data-table__wrapper {
  border: 1px solid #d9e1ec;
  border-radius: 4px;
  overflow-x: auto;
}

.meeting-table >>> table {
  border-collapse: collapse;
}

.meeting-table >>> thead th {
  background: #f2f7ff !important;
  color: #617388 !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  border-right: 1px solid #d9e1ec !important;
  border-bottom: 1px solid #d9e1ec !important;
  height: 40px !important;
}

.meeting-table >>> thead th:last-child {
  border-right: none !important;
}

.meeting-table >>> tbody td {
  border-right: 1px solid #e4eaf3 !important;
  border-bottom: 1px solid #e4eaf3 !important;
  height: 44px !important;
  font-size: 13px;
  color: #2f3e4f;
}

.meeting-table >>> tbody td:last-child {
  border-right: none !important;
}

.meeting-row {
  cursor: pointer;
}

.meeting-row:hover td {
  background: #f8fbff;
}

.meeting-row--expanded td {
  border-bottom: none !important;
  background: #ffffff;
}

.meeting-date-cell {
  display: flex;
  align-items: center;
  font-weight: 500;
}

.meeting-expand-icon {
  color: #6f8caf !important;
}

.action-delete-icon {
  color: #e53935 !important;
  font-size: 18px !important;
}

.delete-icon-btn {
  width: 24px !important;
  height: 24px !important;
  min-width: 24px !important;
  margin: 0 !important;
  flex-shrink: 0;
}

.period-cell {
  display: inline-flex;
  align-items: center;
}

.action-pencil-icon {
  color: #1976d2 !important;
  font-size: 18px !important;
}

.expanded-cell {
  background: #fff;
  border-top: 1px solid #d9e1ec;
}

.expanded-tabs {
  min-height: 44px;
  border-bottom: 1px solid #d9e1ec;
  background: #ffffff;
}

.expanded-tabs >>> .v-tabs-bar {
  height: 44px;
  background: #ffffff !important;
}

.expanded-tabs >>> .v-tab {
  min-width: 96px;
  height: 44px;
  text-transform: uppercase;
  letter-spacing: 0;
  font-size: 12px;
  font-weight: 600;
  color: #7f8ea0 !important;
}

.expanded-tabs >>> .v-tab--active {
  color: #2b6cb0 !important;
}

.expanded-tabs >>> .v-tabs-slider-wrapper {
  height: 2px !important;
}

.expanded-tabs >>> .v-tabs-slider {
  background: #2b6cb0 !important;
}

.expanded-tab-content {
  border-top: 0;
}

.expanded-tab-content >>> .v-window-item {
  padding-top: 0;
}

.theme--dark .meeting-table >>> .v-data-table__wrapper {
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.theme--dark .meeting-table >>> thead th {
  background: rgba(255, 255, 255, 0.06) !important;
  color: rgba(255, 255, 255, 0.87) !important;
  border-right-color: rgba(255, 255, 255, 0.12) !important;
  border-bottom-color: rgba(255, 255, 255, 0.12) !important;
}

.theme--dark .meeting-table >>> tbody td {
  border-right-color: rgba(255, 255, 255, 0.1) !important;
  border-bottom-color: rgba(255, 255, 255, 0.1) !important;
  color: rgba(255, 255, 255, 0.87) !important;
  background-color: var(--v-surface-base) !important;
}

.theme--dark .meeting-row:hover td {
  background-color: rgba(255, 255, 255, 0.05) !important;
}

.theme--dark .meeting-row--expanded td {
  background-color: var(--v-surface-base) !important;
}

.theme--dark .meeting-expand-icon {
  color: rgba(255, 255, 255, 0.55) !important;
}

.theme--dark .expanded-cell {
  background-color: var(--v-surface-base) !important;
  border-top-color: rgba(255, 255, 255, 0.12) !important;
}

.theme--dark .expanded-tabs {
  border-bottom-color: rgba(255, 255, 255, 0.12) !important;
  background-color: var(--v-surface-base) !important;
}

.theme--dark .expanded-tabs >>> .v-tabs-bar {
  background-color: var(--v-surface-base) !important;
}

.theme--dark .expanded-tabs >>> .v-tab {
  color: rgba(255, 255, 255, 0.55) !important;
}

.theme--dark .expanded-tabs >>> .v-tab--active {
  color: var(--v-primary-base) !important;
}

.theme--dark .expanded-tabs >>> .v-tabs-slider {
  background-color: var(--v-primary-base) !important;
}

.theme--dark .expanded-tab-content {
  background-color: var(--v-surface-base);
}
</style>
